/**
 * Quote Response Application Service
 *
 * Orchestrates 2-step workflow for quote response submission:
 * Step 1: File upload (if files exist)
 * Step 2: Quote submission
 *
 * Uses Orval hooks internally (MANDATORY for all API calls)
 * Provides clean hook interface for React components
 *
 * @module QuoteResponseApplicationService
 */

import {
  useQuoteResponsesControllerCreate,
  useQuoteResponsesControllerUploadAttachments,
  type CreateQuoteResponseVersionDto,
  type QuoteAttachmentDto,
  getExternalQuoteRequestsControllerFindAllQueryKey,
  getExternalQuoteRequestsControllerFindRespondedQueryKey,
  getChatControllerGetRoomsQueryKey,
} from "@semo/api-contracts";
import { useQueryClient } from "@tanstack/react-query";
import { QuoteErrorApplicationService } from "./QuoteErrorApplicationService";
import { QuoteErrorType } from "../../../../lib/error-types";

/**
 * Custom hook for quote response workflow orchestration
 *
 * Handles complete workflow:
 * 1. Upload attachments (if any)
 * 2. Submit quote response with attachment IDs
 * 3. Centralized error handling
 * 4. Success notifications
 *
 * @param quoteRequestId - ID of the quote request to respond to
 * @returns Workflow execution function and loading state
 */
export const useQuoteResponseWorkflow = (quoteRequestId: string) => {
  const queryClient = useQueryClient();
  const errorService = QuoteErrorApplicationService.getInstance();

  // Step 1: File upload mutation (Orval hook)
  const { mutateAsync: uploadAttachments, isPending: isUploading } =
    useQuoteResponsesControllerUploadAttachments();

  // Step 2: Quote submission mutation (Orval hook)
  const { mutateAsync: submitQuote, isPending: isSubmitting } =
    useQuoteResponsesControllerCreate();

  /**
   * Execute complete workflow
   * @param quoteData - Quote response data
   * @param files - Files to upload (optional)
   * @throws Error if workflow fails at any step
   */
  const executeWorkflow = async (
    quoteData: CreateQuoteResponseVersionDto,
    files: File[] = [],
  ): Promise<void> => {
    // Step 1: Upload attachments if any
    let uploadedAttachments: QuoteAttachmentDto[] = [];

    if (files.length > 0) {
      try {
        const uploadResult = await uploadAttachments({ data: { files } });
        uploadedAttachments = uploadResult.data || [];

        // Upload success feedback suppressed (only show final success)
      } catch (uploadError) {
        // Classify and handle upload error
        const errorDetail = errorService.classifyAndHandle(
          uploadError,
          QuoteErrorType.UPLOAD_FAILED,
        );
        throw errorDetail;
      }
    }

    // Step 2: Submit quote with attachments
    try {
      await submitQuote({
        quoteRequestId,
        data: {
          ...quoteData,
          attachments:
            uploadedAttachments.length > 0
              ? uploadedAttachments
                  .filter((att) => att.url || att.filePath)
                  .map((att) => ({
                    fileName: att.fileName,
                    url: att.url || att.filePath || "",
                    fileSize: att.fileSize || 0,
                    // Handle both field names for compatibility
                    contentType: att.mimeType || (att as any).contentType,
                  }))
              : undefined,
        },
      });

      // Show success feedback
      errorService.showSuccess();

      // Invalidate relevant queries to refresh UI
      // Use standard prefixes to catch all variations with parameters
      queryClient.invalidateQueries({
        queryKey: getExternalQuoteRequestsControllerFindAllQueryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: getExternalQuoteRequestsControllerFindRespondedQueryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: getChatControllerGetRoomsQueryKey(),
      });
    } catch (submissionError) {
      // Classify and handle submission error
      const errorDetail = errorService.classifyAndHandle(
        submissionError,
        QuoteErrorType.SUBMISSION_FAILED,
      );
      throw errorDetail;
    }
  };

  return {
    executeWorkflow,
    isLoading: isUploading || isSubmitting,
    isUploading,
    isSubmitting,
  };
};
